import { useEffect, useState } from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react';
import { ChevronDownIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import CategoriaPrecioForm from './Categorias/CategoriaPrecioForm';
import axios from '../../axios';
import NoPermissions from '../NoPermissions/NoPermissions';
import Loader from '../../Layout/Loaders/Loader';
import { Modal } from '../UI';
import usePermisos from '../../hooks/usePermisos';
import M from 'materialize-css';

// Permisos requeridos para este componente
const PERMISOS_REQUERIDOS = [
    'products_create',
    'products_read',
    'products_update'
];

// Estilos personalizados para react-select
const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : 'none',
        '&:hover': { borderColor: '#3b82f6' },
        borderRadius: '0.5rem',
        minHeight: '42px',
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: '#dbeafe',
        borderRadius: '0.375rem',
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#1e40af',
        fontWeight: 500,
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#1e40af',
        '&:hover': {
            backgroundColor: '#bfdbfe',
            color: '#1e3a8a',
        },
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
        color: state.isSelected ? 'white' : '#374151',
        '&:active': { backgroundColor: '#dbeafe' },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: '0.5rem',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        zIndex: 100,
    }),
};

const ServiciosCategoriasModal = ({ 
    isOpen, 
    onClose, 
    productoSelected, 
    setProductoSelected, 
    setUpdated 
}) => {
    const [categorias, setCategorias] = useState([]);
    const [selectedCatIds, setSelectedCatIds] = useState([]);
    const [categories, setCategories] = useState([]);
    const [savingCats, setSavingCats] = useState(false);

    // Hook personalizado para permisos
    const { loading: loadingPermisos, tienePermiso } = usePermisos(PERMISOS_REQUERIDOS);

    // Cargar categorías activas cuando se abre el modal
    useEffect(() => {
        if (isOpen) {
            axios.get('api/categories/active/1')
                .then((resp) => {
                    setCategorias(resp.data);
                })
                .catch(e => console.log("Error: ", e));
        }
    }, [isOpen]);

    // Actualizar estado cuando cambia el producto seleccionado
    useEffect(() => {
        if (productoSelected?.id) {
            const tempCatIds = productoSelected.categories?.map(p => p.id) || [];
            setSelectedCatIds(tempCatIds);
            setCategories(productoSelected.categories || []);
        }
    }, [productoSelected]);

    // Cerrar modal y limpiar estado
    const handleClose = () => {
        setSelectedCatIds([]);
        setProductoSelected({});
        onClose();
    };

    // Guardar categorías seleccionadas
    const saveCatsIds = () => {
        setSavingCats(true);
        axios.post(`api/set-product-categories/${productoSelected.id}`, { catIds: selectedCatIds })
            .then((resp) => {
                if (resp.status === 200) {
                    setCategories(resp.data.servicio.categories);
                    setUpdated(true);
                    M.toast({
                        html: `<i class="material-icons">check_circle</i> &nbsp;&nbsp;<span>${resp.data.message}</span>`,
                        classes: "success-notify"
                    });
                }
            })
            .catch(e => console.log("Error: ", e))
            .finally(() => setSavingCats(false));
    };

    // Convertir categorías para react-select
    const categoriasOptions = categorias.map(cat => ({
        value: cat.id,
        label: cat.nombre_categoria
    }));

    // Valores seleccionados para react-select
    const selectedValues = categoriasOptions.filter(opt => 
        selectedCatIds.includes(opt.value)
    );

    // Manejar cambio en select
    const handleCategoryChange = (selected) => {
        setSelectedCatIds(selected ? selected.map(s => s.value) : []);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={null}
            size="xxl"
            showCloseButton={false}
        >
            {loadingPermisos ? (
                <div className="h-48 flex items-center justify-center">
                    <Loader />
                </div>
            ) : !tienePermiso('products_read') ? (
                <NoPermissions />
            ) : (
                <div className="space-y-6">
                    {/* Header del modal */}
                    <div className="border-b border-gray-200 pb-4">
                        <div className="flex items-center justify-between">
                            <div className=''>
                                <h4 className="text-lg font-semibold text-gray-900  uppercase">
                                    Categorías y precios para:
                                </h4>
                                <p className="text-blue-600 font-medium mt-1">
                                    {productoSelected?.nombre_producto}
                                </p>
                            </div>
                            <button
                                onClick={handleClose}
                                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                            >
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                            <span className="font-semibold">Precio de lista:</span>{' '}
                            <span className="text-green-600 font-medium">${productoSelected?.precio_unidad}</span>
                        </p>
                    </div>

                    {/* Selector de categorías */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Categorías
                            </label>
                            <Select
                                isMulti
                                options={categoriasOptions}
                                value={selectedValues}
                                onChange={handleCategoryChange}
                                placeholder="Seleccione las categorías..."
                                noOptionsMessage={() => "No hay categorías disponibles"}
                                styles={selectStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div>
                            <button
                                onClick={saveCatsIds}
                                disabled={savingCats}
                                className="w-full inline-flex cursor-pointer items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {savingCats ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Guardando...
                                    </>
                                ) : (
                                    'Actualizar Categorías'
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Lista de precios por categoría (Accordion) */}
                    {categories && categories.length > 0 && (
                        <div className="border-t border-gray-200 pt-4">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-1 h-8 bg-[#11cdef] rounded-full"></div>
                                <h5 className="text-sm font-semibold text-gray-700">
                                    Listado de precios por categoría
                                </h5>
                            </div>
                            
                            <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                                {categories.map((cat) => (
                                    <Disclosure key={cat.id}>
                                        {({ open }) => (
                                            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
                                                <DisclosureButton className="flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                                    <div className="flex items-center gap-3">
                                                        <CurrencyDollarIcon className="h-5 w-5 text-green-500" />
                                                        <span className="font-medium text-gray-900">
                                                            {cat.nombre_categoria}
                                                        </span>
                                                        <span className="text-cyan-500 font-semibold">
                                                            ${cat.pivot?.precio || '0.00'}
                                                        </span>
                                                    </div>
                                                    <ChevronDownIcon
                                                        className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                                                            open ? 'rotate-180' : ''
                                                        }`}
                                                    />
                                                </DisclosureButton>

                                                <Transition
                                                    enter="transition duration-100 ease-out"
                                                    enterFrom="transform scale-95 opacity-0"
                                                    enterTo="transform scale-100 opacity-100"
                                                    leave="transition duration-75 ease-out"
                                                    leaveFrom="transform scale-100 opacity-100"
                                                    leaveTo="transform scale-95 opacity-0"
                                                >
                                                    <DisclosurePanel className="border-t border-gray-200 bg-gray-50">
                                                        <CategoriaPrecioForm 
                                                            categoria={cat} 
                                                            setProductoSelected={setProductoSelected} 
                                                            setUpdated={setUpdated}
                                                        />
                                                    </DisclosurePanel>
                                                </Transition>
                                            </div>
                                        )}
                                    </Disclosure>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Footer del modal */}
                    <div className="border-t border-gray-200 pt-4 flex justify-end">
                        <button
                            onClick={handleClose}
                            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ServiciosCategoriasModal;